import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { isJoinWindowOpen } from "@/lib/arena-logic";

const SRC_KEY = "arenaSrc";

export type ArenaMe = {
  user_id: string;
  days: number[];
  sticker_given_at: string | null;
  joined_at: string;
};

export type ArenaPerson = {
  user_id: string;
  full_name: string;
  photo_url: string | null;
  profession: string | null;
  about: string | null;
  city: string | null;
  interests: string[] | null;
  looking_for_tags: string[] | null;
  can_help_with: string | null;
  looking_for: string | null;
  industry: string | null;
  industries: string[] | null;
  telegram: string | null;
  social_links: string[] | null;
  days: number[] | null;
};

export type ArenaMeeting = {
  id: string;
  user_a: string;
  user_b: string;
  confirmed_by_a: string | null;
  confirmed_by_b: string | null;
  completed_at: string | null;
};

// Таблицы и функции ARENA вызываются без жёстких типов: файл типов обновляется отдельно.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

export const arenaMeKey = (uid: string | undefined) => ["arena-me", uid] as const;

export async function fetchArenaMe(uid: string): Promise<ArenaMe | null> {
  const { data, error } = await db
    .from("arena_participants")
    .select("user_id, days, sticker_given_at, joined_at")
    .eq("user_id", uid)
    .maybeSingle();
  if (error) throw error;
  return (data as ArenaMe | null) ?? null;
}

/** Моя строка участника ARENA (null — не участник). */
export function useArenaMe() {
  const { user } = useAuth();
  const uid = user?.id;
  return useQuery({
    queryKey: arenaMeKey(uid),
    enabled: !!uid,
    staleTime: 60_000,
    queryFn: () => fetchArenaMe(uid!),
  });
}

export function useArenaCount() {
  return useQuery({
    queryKey: ["arena-count"],
    staleTime: 30_000,
    queryFn: async () => {
      const { data, error } = await db.rpc("arena_count");
      if (error) throw error;
      return (data as number) ?? 0;
    },
  });
}

/** Показывать ли плитку / пункт меню: окно ARENA_JOIN_FROM–ARENA_JOIN_UNTIL. */
export function useArenaWindow(): boolean {
  return isJoinWindowOpen(new Date());
}

export const arenaRpc = {
  join: (src: string | null) => db.rpc("arena_join", { _src: src }),
  setDays: (days: number[]) => db.rpc("arena_set_days", { _days: days }),
  people: () => db.rpc("arena_people"),
  confirm: (other: string) => db.rpc("arena_confirm_meeting", { _other: other }),
  claimSticker: () => db.rpc("arena_claim_sticker"),
  adminOverview: () => db.rpc("arena_admin_overview"),
  myMeetings: () =>
    db
      .from("arena_meetings")
      .select("id, user_a, user_b, confirmed_by_a, confirmed_by_b, completed_at"),
  rewarded: (uid: string) => db.rpc("arena_rewarded_meetings", { uid }),
};

export function storeArenaSrc(src: string | null) {
  if (!src) return;
  try {
    localStorage.setItem(SRC_KEY, src.slice(0, 64));
  } catch {
    /* ignore */
  }
}

export function readArenaSrc(): string | null {
  try {
    return localStorage.getItem(SRC_KEY);
  } catch {
    return null;
  }
}

export function clearArenaSrc() {
  try {
    localStorage.removeItem(SRC_KEY);
  } catch {
    /* ignore */
  }
}
